<template>
  <div class="container-fluid px-4 py-4" style="background:#f8fafc;min-height:100vh;">

    <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:10px;">
      <div>
        <h5 class="text-dark mb-0" style="font-size:21px;font-weight:600;">Attendance</h5>
        <p class="text-secondary mb-0" style="font-size:12px;">First and last recorded activity per employee — not a guarantee of hours worked.</p>
      </div>
      <company-shift-modal></company-shift-modal>
    </div>

    <div class="bg-white border mb-4" style="border-radius:10px;border-color:#e9ecef !important;padding:14px 16px;">
      <div class="d-flex flex-wrap align-items-end" style="gap:16px;">
        <div>
          <div class="text-secondary mb-1" style="font-size:10px;text-transform:uppercase;letter-spacing:.5px;">Date Range</div>
          <date-range-picker :auto-apply="true" :ranges="false" opens="right" v-model="dateRange">
            <template #input="picker">
              <span style="font-size:12px;">
                <i class="bi bi-calendar-event mr-1"></i>
                <template v-if="picker.startDate">{{ formatDate(picker.startDate) }} - {{ formatDate(picker.endDate) }}</template>
                <template v-else>Filter by Date</template>
              </span>
            </template>
          </date-range-picker>
        </div>
        <button class="btn btn-dark btn-sm" @click="fetchAttendance">Filter</button>
        <button class="btn btn-light border btn-sm" @click="resetFilters">Reset</button>
      </div>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
    </div>

    <div v-else class="bg-white border" style="border-radius:12px;border-color:#e9ecef !important;padding:1rem 1.1rem;">
      <b-table hover striped responsive :items="attendance" :fields="fields" @row-clicked="openDetail">
        <template #cell(check_in)="d"><span v-if="d.item.check_in">{{ formatTime(d.item.check_in) }}</span><span v-else class="text-secondary">—</span></template>
        <template #cell(check_out)="d"><span v-if="d.item.check_out">{{ formatTime(d.item.check_out) }}</span><span v-else class="text-secondary">—</span></template>
        <template #cell(status)="d">
          <span class="badge" :class="d.item.check_out ? 'badge-secondary' : 'badge-dark'">{{ d.item.check_out ? 'PRESENT' : 'ACTIVE' }}</span>
          <span v-if="d.item.is_corrected" class="badge badge-secondary ml-1" title="Manually corrected"><i class="bi bi-pencil"></i></span>
        </template>
        <template #cell(actions)="d">
          <div class="dropdown" @click.stop>
            <a href="#" data-toggle="dropdown" aria-expanded="false" class="btn btn-sm btn-link p-0">
              <i class="bi bi-three-dots-vertical"></i>
            </a>
            <div class="dropdown-menu dropdown-menu-right">
              <a class="dropdown-item" href="#" @click.prevent="openDetail(d.item)">View</a>
              <a class="dropdown-item" href="#" @click.prevent="openCorrect(d.item)">Correct</a>
            </div>
          </div>
        </template>
      </b-table>

      <div v-if="attendance.length === 0" class="text-center text-secondary py-4" style="font-size:12px;">
        No attendance records found for this range.
      </div>
    </div>

    <!-- Detail -->
    <b-modal v-model="detailOpen" hide-footer centered title="Attendance Detail">
      <div v-if="detail" style="font-size:13px;">
        <div class="d-flex justify-content-between mb-2"><span class="text-secondary">Employee</span><span class="font-weight-bold">{{ detail.name }}</span></div>
        <div class="d-flex justify-content-between mb-2"><span class="text-secondary">Date</span><span>{{ detail.date }}</span></div>
        <div class="d-flex justify-content-between mb-2"><span class="text-secondary">First activity</span><span>{{ detail.check_in ? formatTime(detail.check_in) : '—' }}</span></div>
        <div class="d-flex justify-content-between mb-2"><span class="text-secondary">Last activity</span><span>{{ detail.check_out ? formatTime(detail.check_out) : '—' }}</span></div>
        <div class="d-flex justify-content-between mb-2"><span class="text-secondary">Device</span><span>{{ detail.device || '—' }}</span></div>
        <div class="d-flex justify-content-between mb-2"><span class="text-secondary">Browser</span><span>{{ detail.browser || '—' }}</span></div>
        <div v-if="detail.is_corrected" class="mt-3 p-2" style="background:#f8fafc;border-radius:8px;font-size:12px;">
          <div class="font-weight-bold mb-1">Corrected</div>
          <div>Original first activity: {{ detail.original_check_in ? formatTime(detail.original_check_in) : '—' }}</div>
          <div>Original last activity: {{ detail.original_check_out ? formatTime(detail.original_check_out) : '—' }}</div>
          <div>By {{ detail.corrected_by }} on {{ detail.corrected_at }}</div>
          <div>Reason: {{ detail.correction_reason }}</div>
        </div>
        <div class="mt-3 text-right">
          <button class="btn btn-dark btn-sm" @click="openCorrect(detail)">Correct</button>
        </div>
      </div>
    </b-modal>

    <!-- Correction -->
    <b-modal v-model="correctOpen" hide-footer centered title="Correct Attendance">
      <form v-if="correctTarget" @submit.prevent="submitCorrection" style="font-size:13px;">
        <div class="form-group">
          <label>First activity</label>
          <input type="datetime-local" class="form-control" v-model="correctForm.check_in">
        </div>
        <div class="form-group">
          <label>Last activity</label>
          <input type="datetime-local" class="form-control" v-model="correctForm.check_out">
        </div>
        <div class="form-group">
          <label>Reason <span class="text-danger">*</span></label>
          <textarea class="form-control" v-model="correctForm.reason" rows="2" required></textarea>
        </div>
        <div v-if="correctError" class="alert alert-danger py-2" style="font-size:12px;">{{ correctError }}</div>
        <div class="text-right">
          <button type="submit" class="btn btn-dark" :disabled="correctSubmitting">{{ correctSubmitting ? 'Saving…' : 'Save Correction' }}</button>
        </div>
      </form>
    </b-modal>

  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
import axios from "axios";
import moment from "moment";
import CompanyShiftModal from '../modal/CompanyShifts.vue'

export default {
  name: "Attendance",
  components: { DateRangePicker, CompanyShiftModal },
  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      attendance: [],
      loading: false,
      dateRange: { startDate: today, endDate: today },
      detail: null,
      detailOpen: false,
      correctTarget: null,
      correctOpen: false,
      correctForm: { check_in: null, check_out: null, reason: "" },
      correctSubmitting: false,
      correctError: null,
      fields: [
        { key: "name", label: "Employee", sortable: true },
        { key: "date", label: "Date", sortable: true },
        { key: "check_in", label: "First Activity" },
        { key: "check_out", label: "Last Activity" },
        { key: "status", label: "Status" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  methods: {
    fetchAttendance() {
      this.loading = true;
      axios.get("/api/attendance-logs", {
        params: {
          start_date: this.dateRange.startDate ? moment(this.dateRange.startDate).format("YYYY-MM-DD") : undefined,
          end_date: this.dateRange.endDate ? moment(this.dateRange.endDate).format("YYYY-MM-DD") : undefined,
        },
      }).then((res) => {
        this.attendance = res.data.data;
      }).catch(() => {
        this.$toasts.push({ type: "danger", message: "Failed to load attendance data." });
      }).finally(() => { this.loading = false; });
    },
    resetFilters() {
      const today = moment().format("YYYY-MM-DD");
      this.dateRange = { startDate: today, endDate: today };
      this.fetchAttendance();
    },
    openDetail(item) {
      this.detail = item;
      this.detailOpen = true;
    },
    openCorrect(item) {
      this.correctTarget = item;
      this.correctForm = {
        check_in: item.check_in ? moment(item.check_in).format("YYYY-MM-DDTHH:mm") : null,
        check_out: item.check_out ? moment(item.check_out).format("YYYY-MM-DDTHH:mm") : null,
        reason: "",
      };
      this.correctError = null;
      this.detailOpen = false;
      this.correctOpen = true;
    },
    submitCorrection() {
      this.correctSubmitting = true;
      this.correctError = null;
      axios.post(`/api/attendance-logs/${this.correctTarget.id}/correct`, this.correctForm)
        .then(() => {
          this.correctOpen = false;
          this.fetchAttendance();
          this.$toasts.push({ type: "success", message: "Attendance corrected." });
        })
        .catch((err) => {
          this.correctError = err.response?.data?.message || "Failed to save correction.";
        })
        .finally(() => { this.correctSubmitting = false; });
    },
    formatTime(datetime) { return moment(datetime).format("HH:mm"); },
    formatDate(date) { return moment(date).format("DD/MM/YYYY"); },
  },
  mounted() {
    this.fetchAttendance();
  },
};
</script>
